import { useState } from "react";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "sonner";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import {
  ChatChannelEligibility,
  ChatChannelKind,
  type SocialUserSearchDto,
} from "Infrastructure/Api/Api";
import { useCreateChatChannelMutation } from "Api/Mutations/Chat/useCreateChatChannelMutation";
import { hasApiProblemError } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Translations/Resources";
import {
  ChatSelectedMembers,
  type SelectedUser,
  toggleSelectedUser,
} from "Components/Chat/ChatSelectedMembers";
import { ChatUserSearch } from "Components/Chat/ChatUserSearch";
import { useSelectedUserIneligibility } from "Hooks/Chat/useSelectedUserIneligibility";
import { useIsFullMoonePersona } from "Hooks/useIsFullMoonePersona";

const RESOURCES = Resources.Chat.NewChat;

const NO_PRESELECTED_USERS: ReadonlyArray<SelectedUser> = [];

/** The radio group reports a plain string; only the enum's own values may reach the state. */
const isChatChannelEligibility = (
  value: string,
): value is ChatChannelEligibility =>
  Object.values<string>(ChatChannelEligibility).includes(value);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onChannelCreated?: (channelId: string) => void;
  /** Members the dialog starts with — e.g. the counterpart when opened from a 1:1 conversation. */
  initialSelectedUsers?: ReadonlyArray<SelectedUser>;
};

/** The message for a refused create: the named refusals get their own, the rest the generic one. */
const getCreateErrorResource = (error: unknown) => {
  if (hasApiProblemError(error, "MemberNotFullMoone")) {
    return RESOURCES.CreateIneligibleMemberError;
  }

  if (hasApiProblemError(error, "MemberHasNoHandle")) {
    return RESOURCES.CreateNoHandleMemberError;
  }

  return RESOURCES.CreateError;
};

/**
 * Creating a GROUP conversation: a name, the people picked so far, and a search that suggests
 * mutuals until you type.
 *
 * One other person is enough. The kind is stated explicitly (ADR-0001), so a group of two is a
 * group — it no longer collapses into a direct conversation the way the old member-count inference
 * made it, and it matches the fact that a group pruned down to two members stays a group.
 *
 * A Full Moone creator may restrict the group to verified (Full Moone) users (App ADR-0012); the
 * option is set once, here. Picked people who would not qualify stay in the row greyed with a note,
 * and Create waits until they are removed or the rule is relaxed — the server refuses them anyway.
 */
export const NewGroupChatDialog = ({
  isOpen,
  onClose,
  onChannelCreated,
  initialSelectedUsers = NO_PRESELECTED_USERS,
}: Props) => {
  const [selectedUsers, setSelectedUsers] = useState<Array<SelectedUser>>([
    ...initialSelectedUsers,
  ]);
  const [channelName, setChannelName] = useState("");
  const [eligibility, setEligibility] = useState<ChatChannelEligibility>(
    ChatChannelEligibility.Anyone,
  );
  const { t } = useResource();
  // Only a Full Moone creator may restrict the group — a Lite owner could not join their own.
  const isRestrictionAvailable = useIsFullMoonePersona();
  const isFullMooneRequired =
    eligibility === ChatChannelEligibility.FullMooneOnly;
  const getIneligibility = useSelectedUserIneligibility(isFullMooneRequired);
  const hasIneligibleSelection = selectedUsers.some(
    (user) => getIneligibility(user) !== undefined,
  );

  const { mutate: createChannel, isPending } = useCreateChatChannelMutation({
    onSuccess: ({ channelId }) => {
      onChannelCreated?.(channelId);
      handleClose();
    },
    // The refusals the dialog cannot pre-empt: a person whose tier or handle it never learned (a
    // counterpart carried in from a 1:1 — the Stream profile carries no handle, and predates the
    // tier field on older profiles) turns out not to be Full Moone, or to have left Chat. Named,
    // so the owner knows what to change; everything else (blocked or unknown members included)
    // gets the generic message. The backend keys these refusals by their error names for exactly
    // that.
    onError: (error) => toast.error(t(getCreateErrorResource(error))),
  });

  const isCreateDisabled =
    selectedUsers.length === 0 || hasIneligibleSelection || isPending;

  const handleSelectUser = (user: SocialUserSearchDto) =>
    setSelectedUsers((prev) => toggleSelectedUser(prev, user, t));

  const handleRemoveUser = (identityUserPublicID: string) => {
    setSelectedUsers((prev) =>
      prev.filter((u) => u.identityUserPublicID !== identityUserPublicID),
    );
  };

  const handleEligibilityChange = (value: string) => {
    if (isChatChannelEligibility(value)) {
      setEligibility(value);
    }
  };

  const handleCreate = () => {
    if (isCreateDisabled) {
      return;
    }

    createChannel({
      memberIdentityUserPublicIDs: selectedUsers.map(
        ({ identityUserPublicID }) => identityUserPublicID,
      ),
      name: channelName.trim() || undefined,
      kind: ChatChannelKind.Group,
      eligibility,
    });
  };

  const handleClose = () => {
    setSelectedUsers([...initialSelectedUsers]);
    setChannelName("");
    setEligibility(ChatChannelEligibility.Anyone);
    onClose();
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t(RESOURCES.GroupTitle)}
      footer={
        <Stack direction="row" justifyContent="flex-end" gap={2}>
          <Button onClick={handleClose}>{t(RESOURCES.Cancel)}</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={isCreateDisabled}
            startIcon={isPending ? <CircularProgress size={20} /> : undefined}
            sx={{ "&.Mui-disabled": { color: "white" } }}
          >
            {t(RESOURCES.Create)}
          </Button>
        </Stack>
      }
    >
      <ChatUserSearch
        selectedUserIDs={selectedUsers.map((u) => u.identityUserPublicID)}
        showsMutualSuggestions
        isFullMooneRequired={isFullMooneRequired}
        isMultiSelect
        onSelectUser={handleSelectUser}
      >
        <ChatSelectedMembers
          selectedUsers={selectedUsers}
          isFullMooneRequired={isFullMooneRequired}
          onRemoveUser={handleRemoveUser}
        />
      </ChatUserSearch>
      <Stack gap={2}>
        <Typography variant="subtitle2">
          {t(RESOURCES.GroupNameLabel)}
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={channelName}
          slotProps={{ htmlInput: { maxLength: 100 } }}
          placeholder={t(RESOURCES.ChannelNamePlaceholder)}
          onChange={(e) => setChannelName(e.target.value)}
        />
      </Stack>
      {isRestrictionAvailable && (
        <Stack gap={1}>
          <Typography variant="subtitle2">
            {t(RESOURCES.EligibilityLabel)}
          </Typography>
          <RadioGroup
            value={eligibility}
            onChange={(_, value) => handleEligibilityChange(value)}
          >
            <FormControlLabel
              value={ChatChannelEligibility.Anyone}
              control={<Radio size="small" />}
              label={t(RESOURCES.EligibilityAnyone)}
            />
            <FormControlLabel
              value={ChatChannelEligibility.FullMooneOnly}
              control={<Radio size="small" />}
              label={t(RESOURCES.EligibilityFullMooneOnly)}
            />
          </RadioGroup>
        </Stack>
      )}
    </ResponsiveDialog>
  );
};
